// Client-side attachment compression for the message Postfach.
//
// Images (JPEG/PNG): resize via Canvas to a max edge length and re-encode as
// JPEG with quality 0.82. Typical 20MB photo → <2MB JPEG with no visible
// quality loss for document scans.
//
// PDF: re-save with pdf-lib using object streams. Strips metadata and merges
// redundant objects (~10-30% smaller). For photo-heavy PDFs this is best-
// effort — if the result is still > 10MB we still send it (backend will
// reject anyway). Caller decides what to do then.

import { PDFDocument } from 'pdf-lib';

const TARGET_MAX_EDGE = 2200; // px
const JPEG_QUALITY = 0.82;
const COMPRESS_TRIGGER_BYTES = 800 * 1024; // skip compression for tiny files

const readAsDataURL = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
  img.src = src;
});

export async function compressImage(file) {
  if (file.size < COMPRESS_TRIGGER_BYTES) return file;

  const dataUrl = await readAsDataURL(file);
  const img = await loadImage(dataUrl);

  const { width, height } = img;
  const maxEdge = Math.max(width, height);
  const scale = maxEdge > TARGET_MAX_EDGE ? TARGET_MAX_EDGE / maxEdge : 1;
  const targetW = Math.round(width * scale);
  const targetH = Math.round(height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  // White background to avoid black bleed when forcing JPEG on PNG with alpha
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(img, 0, 0, targetW, targetH);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
  );
  if (!blob || blob.size >= file.size) return file; // compression made it bigger → keep original

  const baseName = file.name.replace(/\.(png|jpe?g)$/i, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}

export async function compressPdf(file) {
  if (file.size < COMPRESS_TRIGGER_BYTES) return file;

  try {
    const buf = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(buf, { ignoreEncryption: true, updateMetadata: false });
    pdfDoc.setTitle('');
    pdfDoc.setAuthor('');
    pdfDoc.setSubject('');
    pdfDoc.setKeywords([]);
    pdfDoc.setProducer('');
    pdfDoc.setCreator('');
    const bytes = await pdfDoc.save({ useObjectStreams: true, addDefaultPage: false });
    if (bytes.byteLength >= file.size) return file;
    return new File([bytes], file.name, { type: 'application/pdf', lastModified: Date.now() });
  } catch (e) {
    console.warn('PDF Komprimierung fehlgeschlagen, sende Original:', e);
    return file;
  }
}

export async function compressAttachment(file) {
  if (!file) return file;
  if (file.type === 'image/jpeg' || file.type === 'image/png') {
    return compressImage(file);
  }
  if (file.type === 'application/pdf') {
    return compressPdf(file);
  }
  return file;
}
